import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
interface HeroSectionProps {
  isDark: boolean;
}
// The hero image (the LCP element on the home page). We serve two crops so the
// browser only downloads the one it needs: a wide landscape shot on tablet/
// desktop, and a lighter, taller crop on phones.
const HERO_IMAGE_DESKTOP = 'https://cdn.magicpatterns.com/uploads/7pbR7cAfBZEjKm75n961a2/Untitled_design-2.jpg';
const HERO_IMAGE_MOBILE = 'https://cdn.magicpatterns.com/uploads/5p8SAadpusRjjUJUmYipsD/Untitled_design-3.jpg';
export function HeroSection({
  isDark
}: HeroSectionProps) {
  return <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden bg-[#f8f9fb] dark:bg-[#0f1629] transition-colors duration-300" data-id="element-97">
      {/* Background Effects */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-accent-500/8 dark:bg-accent-500/15 rounded-full blur-[140px] -z-10" data-id="element-98"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-primary-500/8 dark:bg-primary-500/15 rounded-full blur-[140px] -z-10" data-id="element-99"></div>

      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8" data-id="element-100">
        {/* Hero tile with image background */}
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl shadow-slate-900/5 dark:shadow-black/30" data-id="element-101">
          {/* Background image — eager + high priority as the LCP element.
           <picture> lets the browser fetch only the crop it needs:
           the wide desktop shot from md (768px) up, the lighter phone crop below. */}
          <picture data-id="element-102">
            <source media="(min-width: 768px)" srcSet={HERO_IMAGE_DESKTOP} data-id="element-103" />
            <img src={HERO_IMAGE_MOBILE} alt="A mortgage strategist mapping out a property portfolio plan on a whiteboard with a client" loading="eager" decoding="async" className="absolute inset-0 w-full h-full object-cover" data-id="element-104" />
          </picture>
          {/* Readability overlay */}
          <div className="absolute inset-0 bg-white/70 dark:bg-[#0f1629]/75" data-id="element-105"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-6 sm:px-10 lg:px-16 py-16 sm:py-20 lg:py-28" data-id="element-106">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none backdrop-blur-sm mb-8" data-id="element-107">
              <span className="flex h-2 w-2 rounded-full bg-primary-500 animate-pulse" data-id="element-108"></span>
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-id="element-109">
                Investor & Mortgage Broker
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-6 leading-[1.1]" data-id="element-110">
              Smarter Property <br className="hidden md:block" data-id="element-111" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-primary-500" data-id="element-112">
                Starts Here!
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-4 text-xl md:text-2xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-12 leading-relaxed font-light" data-id="element-113">
              Whether you're buying your first home or your next investment, we
              help you structure it properly from the start.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-114">
              <Link to="/#contact" className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold text-lg hover:shadow-[0_0_30px_rgba(20,184,166,0.35)] transition-all flex items-center justify-center gap-2 group hover:scale-[1.02]" data-id="element-115">
                Start Your Strategy
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-116" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}